import {
  getDefaultProviderData,
  getProviderTemplateNames,
  isOAuth2OnlyProvider,
  providerTemplates
} from '@/constants/sso-templates';
import { ssoService } from '@/services/sso.service';
import { CreateLoginProviderRequest } from '@/types/sso.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Save,
  Shield,
  TestTube,
  RotateCcw
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiBaseUrl } from '../../config/api.config';
import { FileUploadField } from '@/components/common/form-fields';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

// No resolver: the shared Input/Textarea apply their clean-text rule on top of these field rules
const isHttpUrl = (value?: string | null): boolean => {
  if (!value) return true; // emptiness is the required rule's job
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

// Logo may be an uploaded relative path or a full URL
const isUrlOrPath = (value?: string | null): boolean =>
  !value || value.startsWith('/') || value.startsWith('http://') || value.startsWith('https://');

export default function AddSSOProvider() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  const [imageTimestamp] = useState(() => Date.now());
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    oauth: true,
    advanced: isEditMode, // Expand in edit mode to show all data
    attributes: isEditMode, // Expand in edit mode to show all data
    metadata: isEditMode, // Expand in edit mode to show all data
  });

  // Fetch provider data if in edit mode
  const { data: existingProvider, isLoading: isLoadingProvider } = useQuery({
    queryKey: ['ssoProvider', id],
    queryFn: () => ssoService.getProvider(id!),
    enabled: isEditMode,
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateLoginProviderRequest>({
    // Validate while typing so errors (e.g. clean-text) show under the field, not only after Save
    mode: 'onChange',
    defaultValues: getDefaultProviderData(),
  });

  // Logo is set through FileUploadField (setValue), so register its rule without an input
  register('logo_url', { validate: (value) => isUrlOrPath(value) || 'Must be a valid URL or path' });

  const watchProviderName = watch('provider_name');

  // Load existing provider data
  useEffect(() => {
    if (existingProvider) {
      // Reset form with all provider data
      const formData = {
        provider_name: existingProvider.provider_name || '',
        display_name: existingProvider.display_name || '',
        logo_url: existingProvider.logo_url || '',
        openid_wellknown_url: existingProvider.openid_wellknown_url || '',
        client_id: existingProvider.client_id || '',
        client_secret: existingProvider.client_secret || '',
        redirect_uri: existingProvider.redirect_uri || '',
        scope: existingProvider.scope || 'openid profile email',
        response_type: existingProvider.response_type || 'code',
        enabled: existingProvider.enabled !== undefined ? existingProvider.enabled : true,
        priority: existingProvider.priority !== undefined ? existingProvider.priority : 0,
        allow_signup: existingProvider.allow_signup !== undefined ? existingProvider.allow_signup : true,
        link_existing_accounts: existingProvider.link_existing_accounts !== undefined ? existingProvider.link_existing_accounts : true,
        attribute_mapping: existingProvider.attribute_mapping || {
          email: 'email',
          given_name: 'given_name',
          family_name: 'family_name',
          picture: 'picture',
          sub: 'sub'
        },
        additional_params: existingProvider.additional_params || {},
        metadata: existingProvider.metadata || {}
      };
      
      reset(formData);
      
      // Force update complex fields to ensure they're displayed
      setTimeout(() => {
        if (existingProvider.attribute_mapping) {
          Object.keys(existingProvider.attribute_mapping).forEach(key => {
            setValue(`attribute_mapping.${key}` as any, existingProvider.attribute_mapping![key]);
          });
        }
        if (existingProvider.metadata) {
          Object.keys(existingProvider.metadata).forEach(key => {
            setValue(`metadata.${key}` as any, existingProvider.metadata![key]);
          });
        }
      }, 100);
    }
  }, [existingProvider, reset, setValue]);

  // Auto-generate redirect URI when provider name changes
  useEffect(() => {
    if (watchProviderName && !isEditMode) {
      const redirectUri = ssoService.generateRedirectUri(watchProviderName);
      setValue('redirect_uri', redirectUri);
    }
  }, [watchProviderName, setValue, isEditMode]);

  // Apply template
  useEffect(() => {
    if (selectedTemplate && providerTemplates[selectedTemplate]) {
      const template = providerTemplates[selectedTemplate];
      setValue('provider_name', template.provider_name.toLowerCase());
      setValue('display_name', template.display_name);
      setValue('logo_url', template.logo_url || '');
      setValue('openid_wellknown_url', template.openid_wellknown_url);
      setValue('scope', template.scope);
      setValue('attribute_mapping', template.attribute_mapping);
      setValue('additional_params', template.additional_params || {});
      setValue('metadata', template.metadata || {});
      // Note: redirect_uri will be auto-generated via the watchProviderName effect
    }
  }, [selectedTemplate, setValue]);

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: CreateLoginProviderRequest) => {
      if (isEditMode) {
        return ssoService.updateProvider(id!, data);
      }
      return ssoService.createProvider(data);
    },
    onSuccess: () => {
      toast.success(`Provider ${isEditMode ? 'updated' : 'created'} successfully!`);
      queryClient.invalidateQueries({ queryKey: ['ssoProviders'] });
      navigate('/admin/enterprise/sso-configuration');
    },
    onError: (error: Error) => {
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} provider`);
    },
  });

  // Test configuration mutation
  const testMutation = useMutation({
    mutationFn: async () => {
      if (!id) {
        toast.error('Save the provider first before testing');
        return null;
      }
      return ssoService.testProvider(id);
    },
    onSuccess: (result) => {
      if (result?.success) {
        toast.success('Configuration is valid!');
      } else if (result) {
        toast.error(`Test failed: ${result.error_details.join(', ')}`);
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to test configuration');
    },
  });

  // Sync well-known mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      const url = watch('openid_wellknown_url');
      if (!url) {
        toast.error('Please enter a well-known URL first');
        return null;
      }
      
      // Validate well-known URL
      const isValid = await ssoService.validateWellKnownUrl(url);
      if (!isValid) {
        toast.error('Invalid well-known URL or unable to fetch configuration');
        return null;
      }
      
      if (id) {
        return ssoService.syncWellKnown(id);
      }
      
      toast.success('Well-known URL is valid. Endpoints will be fetched on save.');
      return null;
    },
    onSuccess: (result) => {
      // Result is the updated provider object
      if (result) {
        toast.success('Endpoints synced successfully!');
        queryClient.invalidateQueries({ queryKey: ['ssoProvider', id] });
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to sync well-known configuration');
    },
  });

  const onSubmit = (data: CreateLoginProviderRequest) => {
    // Set defaults if not provided
    let submitData: any = {
      ...data,
      scope: data.scope || 'openid profile email',
      response_type: data.response_type || 'code',
      enabled: data.enabled !== undefined ? data.enabled : true,
      allow_signup: data.allow_signup !== undefined ? data.allow_signup : true,
      link_existing_accounts: data.link_existing_accounts !== undefined ? data.link_existing_accounts : true,
      priority: data.priority || 0,
    };

    // Clean logo_url query string if present
    if (submitData.logo_url) {
      submitData.logo_url = submitData.logo_url.split('?')[0];
    }

    if (!isEditMode) {
      // Only include and lowercase provider_name for new providers
      submitData.provider_name = data.provider_name.toLowerCase();
    } else {
      // Remove provider_name for updates (it can't be changed)
      delete submitData.provider_name;
      
      // Remove client_secret if it's empty or masked (user didn't change it)
      if (!submitData.client_secret || submitData.client_secret === '••••••••') {
        delete submitData.client_secret;
      }
    }
    
    saveMutation.mutate(submitData);
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const apiBaseUrl = getApiBaseUrl();

  if (isEditMode && isLoadingProvider) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className=" space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <button
          onClick={() => navigate('/admin/enterprise/sso-configuration')}
          className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to SSO Configuration
        </button>
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8" />
          <div>
            <h1 className="text-2xl font-bold">
              {isEditMode ? 'Edit SSO Provider' : 'Add SSO Provider'}
            </h1>
            <p className="text-blue-100 mt-1">
              Configure OAuth/OpenID Connect provider for single sign-on
            </p>
          </div>
        </div>
      </div>

      {/* Template Selection (only for new providers) */}
      {!isEditMode && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Setup with Template</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {getProviderTemplateNames().map(name => (
              <button
                key={name}
                type="button"
                onClick={() => setSelectedTemplate(name)}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedTemplate === name
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium capitalize">{name}</div>
              </button>
            ))}
          </div>
          {selectedTemplate && providerTemplates[selectedTemplate]?.instructions && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Setup Instructions</h4>
              <div className="prose prose-sm max-w-none text-blue-800">
                <pre className="whitespace-pre-wrap font-sans text-sm">
                  {providerTemplates[selectedTemplate].instructions}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Configuration */}
        <div className="card">
          <div
            className="p-6 border-b cursor-pointer"
            onClick={() => toggleSection('basic')}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Basic Configuration</h3>
              {expandedSections.basic ? <ChevronDown /> : <ChevronRight />}
            </div>
          </div>
          
          {expandedSections.basic && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('provider_name', {
                      required: 'Provider name is required',
                      pattern: { value: /^[a-z0-9_-]+$/, message: 'Provider name must be lowercase alphanumeric with - or _' },
                    })}
                    className="input-field"
                    placeholder="e.g., google, microsoft, okta"
                    disabled={isEditMode}
                    onChange={(e) => {
                      // Convert to lowercase and remove spaces
                      const value = e.target.value.toLowerCase().replace(/\s+/g, '-');
                      setValue('provider_name', value, { shouldValidate: true });
                    }}
                  />
                  {errors.provider_name && (
                    <p className="text-red-500 text-xs mt-1">{errors.provider_name.message}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Unique identifier (automatically converted to lowercase)
                  </p>
                </div>

                <Input<CreateLoginProviderRequest>
                  name="display_name"
                  control={control}
                  label="Display Name"
                  required
                  type="text"
                  placeholder="e.g., Sign in with Google"
                  rules={{ required: 'Display name is required' }}
                />

                <div>
                  <FileUploadField
                    label="Logo Upload"
                    description="Supported formats: PNG, JPG, GIF, WebP"
                    currentFileUrl={(() => {
                      const url = watch('logo_url');
                      return url ? `${url.split('?')[0]}?t=${imageTimestamp}` : '';
                    })()}
                    onFileChange={(url) => setValue('logo_url', url || '', { shouldValidate: true, shouldDirty: true })}
                    accept="image/*"
                    fileType="image"
                    maxSizeMB={10}
                    metadata={{ category: 'sso-logo', type: 'default' }}
                    error={errors.logo_url?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <input
                    {...register('priority', { valueAsNumber: true, min: { value: 0, message: 'Priority must be 0 or more' } })}
                    type="number"
                    className="input-field"
                    placeholder="0"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Lower numbers appear first
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    {...register('enabled')}
                    type="checkbox"
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">Enabled</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    {...register('allow_signup')}
                    type="checkbox"
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">Allow Sign Up</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    {...register('link_existing_accounts')}
                    type="checkbox"
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">Auto-link Existing Accounts</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* OAuth Configuration */}
        <div className="card">
          <div
            className="p-6 border-b cursor-pointer"
            onClick={() => toggleSection('oauth')}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">OAuth Configuration</h3>
              {expandedSections.oauth ? <ChevronDown /> : <ChevronRight />}
            </div>
          </div>
          
          {expandedSections.oauth && (
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OpenID Well-Known URL {!isOAuth2OnlyProvider(watchProviderName) && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <Input<CreateLoginProviderRequest>
                    name="openid_wellknown_url"
                    control={control}
                    type="url"
                    className="pr-16 sm:pr-16"
                    placeholder="https://accounts.google.com/.well-known/openid-configuration"
                    rules={{
                      validate: (value: string) => {
                        // Required only for OIDC providers; OAuth 2.0-only providers have no well-known URL
                        if (!value) return isOAuth2OnlyProvider(getValues('provider_name')) || 'OpenID well-known URL is required for OIDC providers';
                        return isHttpUrl(value) || 'Must be a valid URL';
                      },
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => syncMutation.mutate()}
                    disabled={syncMutation.isPending}
                    className="absolute right-2 top-[1.4rem] -translate-y-1/2 p-2 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Sync well-known configuration"
                  >
                    <RotateCcw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {isOAuth2OnlyProvider(watchProviderName) && (
                  <p className="text-xs text-gray-500 mt-1">
                    This provider uses OAuth 2.0 (not OpenID Connect)
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input<CreateLoginProviderRequest>
                  name="client_id"
                  control={control}
                  label="Client ID"
                  required
                  type="text"
                  disableRTL
                  placeholder="Your OAuth client ID"
                  rules={{ required: 'Client ID is required' }}
                />

                <div>
                  <Input<CreateLoginProviderRequest>
                    name="client_secret"
                    control={control}
                    label="Client Secret"
                    required={!isEditMode}
                    type="password"
                    placeholder={isEditMode ? "Leave blank to keep existing secret" : "Your OAuth client secret"}
                    // Optional in edit mode: the stored secret is masked and kept when left blank
                    rules={{ required: isEditMode ? false : 'Client Secret is required' }}
                  />
                  {isEditMode && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ For security, the existing secret is not displayed. Enter a new secret to update it, or leave blank to keep the current one.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Redirect URI <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      {...register('redirect_uri', { required: 'Redirect URI is required' })}
                      className="input-field flex-1 bg-gray-50"
                      placeholder="Auto-generated based on provider name"
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const uri = watch('redirect_uri');
                        if (uri) copyToClipboard(uri);
                      }}
                      className="btn btn-outline btn-sm"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  {errors.redirect_uri && (
                    <p className="text-red-500 text-xs mt-1">{errors.redirect_uri.message}</p>
                  )}
                  {watchProviderName ? (
                    <div className="mt-2 p-2 bg-blue-50 rounded-md">
                      <p className="text-xs text-blue-800 font-medium">
                        ✓ Auto-generated callback URL for "{watchProviderName}"
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Add this exact URL to your OAuth provider's allowed redirect URIs
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        <span className="font-medium">API Base:</span> {apiBaseUrl}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Enter a provider name to generate the redirect URI
                    </p>
                  )}
                </div>

                <Input<CreateLoginProviderRequest>
                  name="scope"
                  control={control}
                  label="Scopes"
                  type="text"
                  disableRTL
                  placeholder="openid profile email"
                  helperText="Space-separated OAuth scopes"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Response Type
                  </label>
                  <select {...register('response_type')} className="input-field">
                    <option value="code">code (Authorization Code Flow)</option>
                    <option value="token">token (Implicit Flow)</option>
                    <option value="id_token">id_token</option>
                    <option value="code id_token">code id_token (Hybrid Flow)</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Advanced Configuration */}
        <div className="card">
          <div
            className="p-6 border-b cursor-pointer"
            onClick={() => toggleSection('advanced')}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Advanced Configuration</h3>
              {expandedSections.advanced ? <ChevronDown /> : <ChevronRight />}
            </div>
          </div>
          
          {expandedSections.advanced && (
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional OAuth Parameters (JSON)
                </label>
                <Controller
                  name="additional_params"
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      value={field.value ? JSON.stringify(field.value, null, 2) : ''}
                      onChange={(e) => {
                        try {
                          const parsed = e.target.value ? JSON.parse(e.target.value) : {};
                          field.onChange(parsed);
                        } catch {
                          // Invalid JSON, keep as is
                        }
                      }}
                      className="input-field font-mono text-sm"
                      rows={4}
                      placeholder='{\n  "prompt": "consent",\n  "access_type": "offline"\n}'
                    />
                  )}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Provider-specific parameters (e.g., tenant for Azure AD)
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Attribute Mapping */}
        <div className="card">
          <div
            className="p-6 border-b cursor-pointer"
            onClick={() => toggleSection('attributes')}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Attribute Mapping</h3>
              {expandedSections.attributes ? <ChevronDown /> : <ChevronRight />}
            </div>
          </div>
          
          {expandedSections.attributes && (
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Map provider fields to user attributes. Default values work for most OpenID Connect providers.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input<CreateLoginProviderRequest>
                  name="attribute_mapping.email"
                  control={control}
                  label="Email Field"
                  type="text"
                  disableRTL
                  placeholder="email"
                />

                <Input<CreateLoginProviderRequest>
                  name="attribute_mapping.given_name"
                  control={control}
                  label="First Name Field"
                  type="text"
                  disableRTL
                  placeholder="given_name"
                />

                <Input<CreateLoginProviderRequest>
                  name="attribute_mapping.family_name"
                  control={control}
                  label="Last Name Field"
                  type="text"
                  disableRTL
                  placeholder="family_name"
                />

                <Input<CreateLoginProviderRequest>
                  name="attribute_mapping.picture"
                  control={control}
                  label="Picture Field"
                  type="text"
                  disableRTL
                  placeholder="picture"
                />

                <Input<CreateLoginProviderRequest>
                  name="attribute_mapping.sub"
                  control={control}
                  label="Subject/User ID Field"
                  type="text"
                  disableRTL
                  placeholder="sub"
                />
              </div>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="card">
          <div
            className="p-6 border-b cursor-pointer"
            onClick={() => toggleSection('metadata')}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Provider Metadata</h3>
              {expandedSections.metadata ? <ChevronDown /> : <ChevronRight />}
            </div>
          </div>
          
          {expandedSections.metadata && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input<CreateLoginProviderRequest>
                  name="metadata.provider_type"
                  control={control}
                  label="Provider Type"
                  type="text"
                  placeholder="e.g., google, microsoft"
                />

                <Input<CreateLoginProviderRequest>
                  name="metadata.company"
                  control={control}
                  label="Company"
                  type="text"
                  placeholder="e.g., Google Inc"
                />

                <Input<CreateLoginProviderRequest>
                  name="metadata.support_url"
                  control={control}
                  label="Support URL"
                  type="url"
                  placeholder="https://support.example.com"
                  rules={{ validate: (value: string) => isHttpUrl(value) || 'Must be a valid URL starting with http:// or https://' }}
                />

                <Input<CreateLoginProviderRequest>
                  name="metadata.domain"
                  control={control}
                  label="Domain"
                  type="text"
                  disableRTL
                  placeholder="e.g., your-domain.okta.com"
                />

                <div className="md:col-span-2">
                  <Textarea<CreateLoginProviderRequest>
                    name="metadata.description"
                    control={control}
                    label="Description"
                    rows={3}
                    placeholder="Additional notes about this provider configuration"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 -mx-6 -mb-6 rounded-b-xl">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/admin/enterprise/sso-configuration')}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Cancel
            </button>

            <div className="flex items-center gap-3">
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => testMutation.mutate()}
                  disabled={testMutation.isPending}
                  className="px-4 py-2 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TestTube className={`w-4 h-4 ${testMutation.isPending ? 'animate-pulse' : ''}`} />
                  Test Configuration
                </button>
              )}

              <button
                type="submit"
                disabled={isSubmitting || saveMutation.isPending}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(isSubmitting || saveMutation.isPending) ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isEditMode ? 'Update Provider' : 'Create Provider'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}